import { Box, Typography } from '@mui/material';

interface HeroData {
  id: number;
  title: string;
  content: string | null;
  backgroundImage?: {
    id: number;
    url: string;
    alternativeText?: string | null;
  } | null;
}

interface AboutHeroSectionProps {
  heroData?: HeroData;
  getImageUrl: (image: any) => string;
  getImageAlt: (image: any) => string;
}

function AboutHeroSection({
  heroData,
  getImageUrl,
  getImageAlt,
}: AboutHeroSectionProps) {
  return (
    <Box
      sx={{
        backgroundColor: 'white',
        width: '100%',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Main container - Full width with proper overlap layout */}
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          minHeight: { xs: 'auto', md: '679px' },
        }}
      >
        {/* Image - Floating over content background (top layer) */}
        {heroData?.backgroundImage?.url && (
          <Box
            sx={{
              position: { xs: 'relative', md: 'absolute' },
              width: { xs: '100%', md: '600px' }, // Smaller than content area
              height: { xs: '300px', md: '500px' }, // Smaller height
              borderRadius: { xs: '0px', md: '0px 10px 10px 0px' },
              overflow: 'hidden',
              left: 0, // Position to overlap content
              top: { xs: 0, md: '50%' },
              transform: { xs: 'none', md: 'translateY(-50%)' },
              zIndex: 2, // Above content background, below text
            }}
          >
            <Box
              component='img'
              src={getImageUrl(heroData.backgroundImage)}
              alt={getImageAlt(heroData.backgroundImage)}
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
            />
          </Box>
        )}

        {/* Content Box - Full width background (bottom layer) */}
        <Box
          sx={{
            backgroundColor: '#F7F7F7',
            borderRadius: { xs: '12px', md: '19px' },
            width: '100%',
            minHeight: { xs: 'auto', md: '679px' },
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            p: { xs: 3, sm: 4, md: 6, lg: 8 },
            position: 'relative',
            zIndex: 1, // Bottom layer
          }}
        >
          {/* Content wrapper with proper positioning */}
          <Box
            sx={{
              maxWidth: { xs: '100%', md: '712px' },
              ml: { xs: 0, md: '600px' }, // Start content to the right of image overlap
            }}
          >
            {/* Title - Matching design exactly */}
            <Typography
              variant='h1'
              component='h1'
              sx={{
                fontFamily: 'Radio Canada, sans-serif',
                fontWeight: 700,
                fontSize: { xs: '28px', sm: '32px', md: '40px' },
                lineHeight: 1.2,
                color: '#001A44',
                mb: { xs: 2, md: 3 },
                textAlign: { xs: 'center', md: 'left' },
              }}
            >
              {heroData?.title || 'MỤC TIÊU - SỨ MỆNH'}
            </Typography>

            {/* Orange line - Exact design dimensions */}
            <Box
              sx={{
                width: '169px',
                height: '5px',
                backgroundColor: '#EB722E',
                mb: { xs: 3, md: 4 },
                mx: { xs: 'auto', md: 0 },
              }}
            />

            {/* Content - Matching design styling */}
            <Typography
              variant='body1'
              sx={{
                fontFamily: 'Roboto, sans-serif',
                fontSize: { xs: '15px', sm: '16px' },
                lineHeight: 1.5,
                color: '#333333',
                textAlign: { xs: 'center', md: 'left' },
                whiteSpace: 'pre-line',
                maxWidth: '712px',
              }}
            >
              {heroData?.content ||
                `Công ty TNHH Cơ Khí Chính Xác Bách Việt là đơn vị chuyên gia công cơ khí chính xác tại Hà Nội. Với đội ngũ kỹ thuật viên lành nghề và kinh nghiệm lâu năm trong ngành, Bách Việt chuyên sản xuất các linh kiện, phụ tùng và chi tiết máy móc theo yêu cầu, bao gồm trục vít, xi lanh, tản nhiệt và nhiều sản phẩm cơ khí khác. Công ty cam kết mang đến cho khách hàng những giải pháp kỹ thuật tối ưu, đáp ứng nhu cầu đa dạng của các ngành công nghiệp.

Chúng tôi luôn nỗ lực hết mình để xây dựng một thương hiệu uy tín, một địa chỉ tin cậy của các đối tác trong và ngoài nước với 5 tiêu chí:

Độ chính xác cao, công nghệ hiện đại
Kinh nghiệm lâu năm, đội ngũ chuyên nghiệp
Gia công theo yêu cầu – đa dạng sản phẩm

Giá cả cạnh tranh – tối ưu chi phí

Uy tín, đúng tiến độ, hỗ trợ tận tâm`}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default AboutHeroSection;
